import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, HStack, Icon, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronRight } from 'lucide-react-native';

import type { ReasonRow } from '@/admin/dashboards/chartViewModel';

export type ReasonListProps = {
  reasons: ReasonRow[] | null | undefined;
  testID?: string;
};

/**
 * Collapsible thumbs-down reasons list (starts collapsed).
 */
export function ReasonList({ reasons, testID = 'thumbs-down-reasons' }: ReasonListProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const items = reasons ?? [];
  const count = items.length;

  return (
    <Box mt="$2" testID={testID}>
      <Pressable
        onPress={() => setExpanded((open) => !open)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        testID={`${testID}-toggle`}
      >
        <HStack alignItems="center" space="xs">
          <Icon
            as={expanded ? ChevronDown : ChevronRight}
            size="sm"
            color="$textSubtle"
            testID={`${testID}-chevron`}
          />
          <Text size="sm" color="$textSubtle">
            {expanded
              ? t('admin.analytics.feedbackHelpfulness.reasonsCollapsedHide', { count })
              : t('admin.analytics.feedbackHelpfulness.reasonsCollapsedShow', { count })}
          </Text>
        </HStack>
      </Pressable>
      {!!expanded && (
        <VStack space="xs" mt="$1" testID={`${testID}-list`}>
          {items.map((item) => (
            <Text key={item.label} size="sm" color="$textBody" testID={`${testID}-item-${item.label}`}>
              {t('admin.analytics.feedbackHelpfulness.reasonLine', {
                label: item.label,
                count: Math.round(item.count),
              })}
            </Text>
          ))}
        </VStack>
      )}
    </Box>
  );
}
